import { motion } from 'framer-motion'
import { useState } from 'react'
import { Target, Users, Award, Zap, Heart, Shield, TrendingUp, BookOpen, CheckCircle } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'


export default function AboutPage() {
    const [activeTab, setActiveTab] = useState('mission')

    const values = [
        {
            icon: Target,
            title: 'Excellence',
            description: 'We strive for excellence in every aspect of NEET preparation',
            color: 'from-blue-500 to-cyan-500',
        },
        {
            icon: Users,
            title: 'Community',
            description: 'Building a supportive community of future medical professionals',
            color: 'from-purple-500 to-pink-500',
        },
        {
            icon: Award,
            title: 'Results',
            description: '95% success rate speaks for our commitment to your success',
            color: 'from-amber-500 to-orange-500',
        },
        {
            icon: Zap,
            title: 'Innovation',
            description: 'Cutting-edge technology for modern exam preparation',
            color: 'from-green-500 to-emerald-500',
        },
        {
            icon: Heart,
            title: 'Care',
            description: 'Personalized attention and mentorship for every student',
            color: 'from-red-500 to-rose-500',
        },
        {
            icon: Shield,
            title: 'Trust',
            description: 'Transparent processes and proven methodologies',
            color: 'from-indigo-500 to-violet-500',
        },
    ]



    const team = [
        {
            name: 'Ram Charan',
            role: 'Founder & CEO',
            qualification: 'AIIMS Delhi, AIR 23',
            image: null,
        },
        {
            name: 'Arunkumar',
            role: 'Academic Director',
            qualification: 'JIPMER, AIR 45',
            image: null,
        },
        {
            name: 'Karthik',
            role: 'Head of Mentorship',
            qualification: 'MAMC Delhi, AIR 67',
            image: null,
        },
    ]

    return (
        <div className="min-h-screen relative">

            {/* Hero Section */}
            <section className="relative pt-32 pb-24 overflow-hidden">


                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            About Us
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">Empowering Future</span>
                            <span className="block mt-2 gradient-text">Medical Professionals</span>
                        </h1>
                        <p className="text-xl text-theme-secondary leading-relaxed">
                            Future Founder is India's most comprehensive NEET preparation platform,
                            combining expert mentorship, advanced technology, and proven strategies
                            to help students achieve their medical career dreams.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { icon: Users, value: '10,000+', label: 'Students Enrolled' },
                            { icon: Award, value: '95%', label: 'Success Rate' },
                            { icon: BookOpen, value: '500+', label: 'Expert Mentors' },
                            { icon: TrendingUp, value: '50,000+', label: 'Hours of Content' },
                        ].map((stat, index) => {
                            const Icon = stat.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 text-center hover:scale-105 transition-all duration-300">
                                        <Icon className="h-8 w-8 text-primary-blue mx-auto mb-3" />
                                        <p className="text-3xl font-bold gradient-text mb-1">{stat.value}</p>
                                        <p className="text-sm text-theme-muted">{stat.label}</p>
                                    </GlassmorphicCard>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Mission/Vision Tabs */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto">
                        {/* Tab Buttons */}
                        <div className="flex justify-center gap-4 mb-12">
                            {['mission', 'vision', 'approach'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-8 py-3 rounded-xl font-semibold capitalize transition-all duration-300 ${activeTab === tab
                                        ? 'bg-primary-blue text-white'
                                        : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <GlassmorphicCard variant="elevated" className="p-12">
                                {activeTab === 'mission' && (
                                    <div>
                                        <h2 className="text-4xl font-display font-bold text-theme-primary mb-6">
                                            Our <span className="gradient-text">Mission</span>
                                        </h2>
                                        <p className="text-lg text-theme-secondary mb-6 leading-relaxed">
                                            To democratize quality NEET preparation by providing world-class mentorship,
                                            innovative learning tools, and a supportive community to every aspiring medical student.
                                        </p>
                                        <p className="text-lg text-theme-secondary leading-relaxed">
                                            We believe that with the right guidance, resources, and dedication,
                                            every student can achieve their dream of becoming a doctor.
                                        </p>
                                    </div>
                                )}
                                {activeTab === 'vision' && (
                                    <div>
                                        <h2 className="text-4xl font-display font-bold text-theme-primary mb-6">
                                            Our <span className="gradient-text">Vision</span>
                                        </h2>
                                        <p className="text-lg text-theme-secondary mb-6 leading-relaxed">
                                            To become India's most trusted and effective NEET preparation platform,
                                            setting new standards in medical entrance exam coaching through technology
                                            and personalized mentorship.
                                        </p>
                                        <p className="text-lg text-theme-secondary leading-relaxed">
                                            We envision a future where every deserving student has access to premium
                                            NEET preparation, regardless of their geographical or economic background.
                                        </p>
                                    </div>
                                )}
                                {activeTab === 'approach' && (
                                    <div>
                                        <h2 className="text-4xl font-display font-bold text-theme-primary mb-6">
                                            Our <span className="gradient-text">Approach</span>
                                        </h2>
                                        <div className="space-y-4">
                                            {[
                                                'Personalized learning paths based on individual strengths and weaknesses',
                                                'One-on-one mentorship from NEET toppers and medical professionals',
                                                'Advanced analytics to track progress and optimize study strategies',
                                                'Comprehensive test series with detailed performance analysis',
                                                'Regular doubt-clearing sessions and interactive live classes',
                                                'Focus on conceptual clarity rather than rote memorization',
                                            ].map((point, index) => (
                                                <div key={index} className="flex items-start gap-3">
                                                    <CheckCircle className="h-6 w-6 text-primary-blue flex-shrink-0 mt-1" />
                                                    <p className="text-lg text-theme-secondary">{point}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </GlassmorphicCard>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Our <span className="gradient-text">Core Values</span>
                        </h2>
                        <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
                            The principles that guide everything we do
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {values.map((value, index) => {
                            const Icon = value.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 h-full hover:scale-105 transition-all duration-300 group relative overflow-hidden">
                                        <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                                        <div className="relative z-10">
                                            <div className="p-3 rounded-xl bg-primary-blue/20 text-primary-blue w-fit mb-4 group-hover:bg-primary-blue group-hover:text-white transition-all">
                                                <Icon className="h-6 w-6" />
                                            </div>
                                            <h3 className="text-xl font-display font-semibold text-theme-primary mb-3">
                                                {value.title}
                                            </h3>
                                            <p className="text-theme-muted leading-relaxed">
                                                {value.description}
                                            </p>
                                        </div>
                                    </GlassmorphicCard>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>



            {/* Leadership Team */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Meet Our <span className="gradient-text">Leadership</span>
                        </h2>
                        <p className="text-lg text-theme-secondary">
                            Experienced professionals dedicated to your success
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        {team.map((member, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassmorphicCard className="p-6 text-center hover:scale-105 transition-all duration-300">
                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold p-1 mx-auto mb-4">
                                        <div className="w-full h-full rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                                            <span className="text-3xl font-bold gradient-text">
                                                {member.name.charAt(0)}
                                            </span>
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-display font-semibold text-theme-primary mb-2">
                                        {member.name}
                                    </h3>
                                    <p className="text-sm text-primary-blue mb-2">{member.role}</p>
                                    <p className="text-xs text-theme-muted">{member.qualification}</p>
                                </GlassmorphicCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    )
}
